<!--
  The MCP page: what this server offers an AI assistant, how to point one at
  it, and the tokens that let it in.

  This was the API tokens page. Minting a token was never the point -- it is
  the last two minutes of a job whose first twenty are "what is this, and what
  do I do with it". The page now answers those first and keeps token
  management as its final section.

  One thing is worth saying plainly, because it is the expectation everyone
  arrives with: this MCP server is not a program. Almost every MCP server a
  person meets is a local process their client starts with npx, so "install"
  is the assumed first step. There is nothing to install here. The server is
  the Fastify app serving this very page, answering at /mcp over streamable
  HTTP, stateless, with a bearer token. A URL and a token is the whole setup.

  The endpoint URL is built from location.origin rather than written down, so
  the snippets on this page are right on localhost, on a preview host and in
  production without anyone remembering to edit them.

  The token half keeps the contract the old page was built around:

    GET    /v1/tokens          -> 200 {tokens:[{id,label,createdAt,lastUsedAt,agentId}]}
    POST   /v1/tokens {label}  -> 201 {id,label,token}   // plaintext, once
    DELETE /v1/tokens/:id      -> 200 {ok:true,revoked:boolean}

  and the three things about those endpoints that shape the code:

  1. They accept ONLY the session cookie. A request carrying an Authorization
     header is refused even when a valid cookie rides alongside it, so that a
     leaked agent token can never mint more tokens for itself. Every call here
     therefore passes `sessionOnly: true`, which suppresses the developer
     bearer the shared api() helper would otherwise attach from localStorage.

  2. A bad label is a 400 with code 'unknown_key' and path ['label'] -- a field
     error, not a page error, so it is rendered under the input. The server
     caps labels at 80 characters and refuses `<` and `>` outright rather than
     escaping them, so the input validates to the same rule and says so before
     the round trip.

  3. `revoked` is meaningful. Every failure case -- not a uuid, no such token,
     someone else's token, already revoked -- still answers {ok:true,
     revoked:false}. So the flag is read rather than assumed, and the list is
     refetched either way.

  If the endpoints 404 (a route Fastify does not have, with no refusal body)
  that is "not deployed here yet", and the page says so plainly instead of
  showing an error nobody can act on.

  The plaintext token never leaves this component's memory. It may be dropped
  into the connect snippets while it is on screen, at the person's asking, and
  it goes when they dismiss the reveal. It is not written to localStorage,
  sessionStorage, the URL or anywhere else -- the server will not show it
  again, and neither will this page.
-->
<script lang="ts">
  import { onMount } from 'svelte'
  import {
    ApiError,
    api,
    describeError,
    inWords,
    isAuthError,
    isNotDeployed,
    isRateLimited,
  } from '../lib/api'
  import { redirectToLogin } from '../lib/login'
  import { navigate } from '../lib/router.svelte'
  import CopyBox from '../lib/components/CopyBox.svelte'
  import TopBar from '../lib/components/TopBar.svelte'
  import type { ApiToken } from '../lib/types'

  /** The server's own ceiling. Kept in step with it deliberately. */
  const LABEL_MAX = 80

  /**
   * What stands in for a token in the snippets.
   *
   * It keeps the real prefix (`udt_`, which createApiToken mints) so the shape
   * is recognisable, and is loud enough that pasting it unedited fails in a
   * way the person can read rather than half-working.
   */
  const TOKEN_PLACEHOLDER = 'udt_YOUR_TOKEN_HERE'

  type List =
    | { kind: 'loading' }
    | { kind: 'ready'; tokens: ApiToken[] }
    | { kind: 'unavailable' }
    | { kind: 'stuck' }
    | { kind: 'error'; message: string }

  let list = $state<List>({ kind: 'loading' })

  let label = $state('')
  let minting = $state(false)
  /** Belongs under the input: something about the label itself. */
  let labelError = $state('')
  /** Belongs above the list: something about the request. */
  let mintError = $state('')

  /**
   * The plaintext token, held in one local variable for exactly as long as it
   * is on screen. See the note at the top of this file.
   */
  let minted = $state<{ id: string; label: string; token: string } | null>(null)
  let copied = $state<string | null>(null)

  /**
   * Whether the connect snippets carry the just-minted token instead of the
   * placeholder. Off until the person asks for it: the snippets sit near the
   * top of a page people leave open, and a live credential should not be
   * sitting there by default.
   */
  let useMinted = $state(false)

  let confirming = $state<string | null>(null)
  let revoking = $state<string | null>(null)
  let revokeNote = $state('')
  let revokeError = $state('')

  /* --------------------------------------------------------------- connect */

  /**
   * The endpoint, from the page's own origin.
   *
   * Never a hardcoded host: the same build serves localhost:8787 and
   * production, and a snippet naming the wrong one is a support ticket.
   */
  const mcpUrl = $derived(`${location.origin}/mcp`)

  /** Real only while the reveal is up AND the person asked for it. */
  const snippetToken = $derived(useMinted && minted ? minted.token : TOKEN_PLACEHOLDER)
  const snippetIsLive = $derived(useMinted && minted !== null)

  const claudeCodeCommand = $derived(
    `claude mcp add --transport http unomena-docs ${mcpUrl} \\\n  --header "Authorization: Bearer ${snippetToken}"`,
  )

  const jsonConfig = $derived(`{
  "mcpServers": {
    "unomena-docs": {
      "type": "http",
      "url": "${mcpUrl}",
      "headers": {
        "Authorization": "Bearer ${snippetToken}"
      }
    }
  }
}`)

  /**
   * The prompt to hand an assistant.
   *
   * The server already sends SERVER_INSTRUCTIONS on connect, so this is not a
   * copy of it -- it is the human-facing half: what the assistant can do here,
   * the skeleton-first habit, one section per call, findings in the document
   * rather than the chat, and the four things (folders, sharing, ownership,
   * deleting) that are the person's to do in the browser and have no tools.
   * Deliberately free of headings and formatting so it survives being pasted
   * into any chat box.
   */
  const ASSISTANT_PROMPT = `You have the unomena-docs MCP server connected. It holds living documents that I can see in my browser while you write them.

When I ask you for a document, make it there rather than as a local file, even when we are working in code. Create it FIRST, before you search, read files or plan: the first call is a skeleton — the title, and one pending section per part you intend to write — so I can see the shape of it immediately. Then research, and fill the sections one call at a time, each one a heading and its body. A call shows nothing until it finishes, so writing the whole document in a single call leaves me watching a blank page and then a wall of text.

Findings go in the document, not in the chat. Keep me posted with a short line naming what you are on, and send me the link once, when the document exists — the link, never the document pasted back at me.

If you are blocked on something I have not told you, do not guess: ask in the document, with two or three suggested options, then wait once and stop. I will answer; you must not answer your own question. If I leave a comment for you, reply in that thread in a sentence or two.

Filing documents into folders, sharing them, changing who owns them and deleting them are mine to do in the browser — there are no tools for any of it, by design. If I ask you to file, share, hand over or delete a document, tell me it is done from the document list.`

  /* ---------------------------------------------------------------- tokens */

  /** The same rule the server applies, so the common mistake never round-trips. */
  function labelProblem(value: string): string {
    if (!value) return 'Give the token a label so you can tell it apart later.'
    if (value.length > LABEL_MAX) return `Keep the label to ${LABEL_MAX} characters or fewer.`
    if (/[<>]/.test(value)) return 'Labels cannot contain < or >.'
    return ''
  }

  /** True when a refusal is about the label field rather than the request. */
  function isFieldError(err: unknown): boolean {
    return err instanceof ApiError && err.status === 400 && (err.path || [])[0] === 'label'
  }

  async function load() {
    try {
      const res = await api<{ tokens: ApiToken[] }>('/v1/tokens', { sessionOnly: true })
      list = { kind: 'ready', tokens: res.tokens ?? [] }
    } catch (err) {
      if (isNotDeployed(err)) {
        list = { kind: 'unavailable' }
        return
      }
      if (isAuthError(err)) {
        if (redirectToLogin()) return
        list = { kind: 'stuck' }
        return
      }
      list = { kind: 'error', message: `Could not load your tokens: ${describeError(err)}.` }
    }
  }

  onMount(load)

  /** Forget the plaintext, and with it anything on screen that carried it. */
  function forget() {
    minted = null
    useMinted = false
  }

  async function mint(event: Event) {
    event.preventDefault()
    if (minting) return

    const value = label.trim()
    const problem = labelProblem(value)
    if (problem) {
      labelError = problem
      return
    }

    minting = true
    labelError = ''
    mintError = ''
    copied = null
    useMinted = false
    try {
      const res = await api<{ id: string; label: string; token: string }>('/v1/tokens', {
        method: 'POST',
        sessionOnly: true,
        body: JSON.stringify({ label: value }),
      })
      if (!res.token) {
        // A 201 without the plaintext would leave the person believing they
        // have a credential they can never see. Say so instead.
        mintError = 'The server created the token but did not return it. Revoke it and try again.'
      } else {
        minted = { id: res.id, label: res.label ?? value, token: res.token }
        label = ''
      }
      await load()
    } catch (err) {
      if (isFieldError(err)) {
        labelError = (err as ApiError).detail || 'That label was refused.'
      } else if (isRateLimited(err)) {
        const wait = err instanceof ApiError ? err.retryAfter : 0
        mintError = `That is as many tokens as this account may mint for now. Try again ${inWords(wait)}.`
      } else if (isNotDeployed(err)) {
        mintError = 'Token minting is not available on this server yet.'
      } else {
        mintError = `Could not mint a token: ${describeError(err)}.`
      }
    } finally {
      minting = false
    }
  }

  async function revoke(id: string, name: string) {
    revoking = id
    revokeError = ''
    revokeNote = ''
    try {
      const res = await api<{ ok: boolean; revoked: boolean }>(`/v1/tokens/${id}`, {
        method: 'DELETE',
        sessionOnly: true,
      })
      // The flag, not the status. A token that was already gone, or was never
      // this account's, answers 200 {ok:true, revoked:false}.
      revokeNote = res.revoked
        ? `Revoked “${name}”. Any agent using it will stop working now.`
        : `“${name}” was already gone — nothing to revoke.`
      if (minted?.id === id) forget()
      confirming = null
    } catch (err) {
      revokeError = `Could not revoke that token: ${describeError(err)}.`
    } finally {
      revoking = null
      // Either way the list on screen may now be wrong, so refetch it.
      await load()
    }
  }

  async function copy(text: string, what: string) {
    try {
      await navigator.clipboard.writeText(text)
      copied = what
      setTimeout(() => {
        if (copied === what) copied = null
      }, 2000)
    } catch {
      // No clipboard permission, or an insecure origin. The box below is
      // `user-select: all`, so one click still selects the whole thing.
      copied = null
      mintError = 'Could not reach the clipboard — select the text and copy it by hand.'
    }
  }

  /** Drop the fresh token into the snippets and take the person back to them. */
  function useInSnippets() {
    useMinted = true
    document.getElementById('connect')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  function when(iso: string | null): string {
    if (!iso) return 'never'
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
  }
</script>

<svelte:head><title>MCP · Unomena Docs</title></svelte:head>

<TopBar />

<main>
  <div class="measure measure-wide">
    <p class="eyebrow">Model Context Protocol</p>
    <h1 class="page-title">MCP</h1>
    <p class="page-lede">
      Connect an AI assistant to this server and it writes documents here, with you watching them
      fill.
    </p>

    <!-- 1 ------------------------------------------------------ what this is -->
    <section class="prose" aria-labelledby="what">
      <div class="section-head"><h2 id="what">What this is</h2></div>
      <p>
        MCP is how an AI assistant reaches a system it did not ship with. Point one at this server
        and it can create documents here, write them a section at a time, read them back, and ask
        you questions inside them. They are the same documents you see in the browser — it writes,
        the page you are looking at fills in.
      </p>
      <p>
        <strong>There is nothing to install.</strong> Most MCP servers you meet are a program your client
        starts on your machine with <code>npx</code>; this one is not a program at all. It is this server
        — the one that served this page — answering at one more address. Setting it up is a URL and a
        token, and that is the whole of it.
      </p>
      <p>
        Each token is one credential for one client. The server checks it on every call and can see
        only what you can see, so an assistant holding your token reads and writes exactly the
        documents you do, and nothing else.
      </p>
    </section>

    <!-- 2 ---------------------------------------------------------- connect -->
    <section class="prose" aria-labelledby="connect">
      <div class="section-head"><h2 id="connect">Connect</h2></div>
      <p>
        The transport is streamable HTTP and the credential is a bearer token in the
        <code>Authorization</code> header. Any client that speaks those two can connect.
      </p>

      <CopyBox label="Endpoint" text={mcpUrl} />

      <div class="token-swap">
        {#if snippetIsLive}
          <p class="token-swap-live" role="status">
            The snippets below carry the token you just minted. It is not saved anywhere — leaving
            this page loses it.
          </p>
          <button type="button" class="btn btn-quiet btn-small" onclick={() => (useMinted = false)}>
            Put the placeholder back
          </button>
        {:else if minted}
          <p class="token-swap-offer">You minted a token a moment ago.</p>
          <button type="button" class="btn btn-quiet btn-small" onclick={() => (useMinted = true)}>
            Use it in these snippets
          </button>
        {:else}
          <p class="token-swap-offer">
            The snippets carry a placeholder. Swap in a real token — mint one in
            <a href="#tokens">API tokens</a> below.
          </p>
        {/if}
      </div>

      <CopyBox
        label="Claude Code"
        text={claudeCodeCommand}
        hint="Run it in a terminal. Add --scope user to make it available in every project."
      />

      <CopyBox
        label="Any client that takes mcpServers config"
        text={jsonConfig}
        hint="Claude Desktop, Cursor, Windsurf and friends. Some spell the transport key transport rather than type; the url and the header are the same either way."
      />
    </section>

    <!-- 3 ------------------------------------------------------ the prompt -->
    <section class="prose" aria-labelledby="prompt">
      <div class="section-head"><h2 id="prompt">A prompt to give your assistant</h2></div>
      <p>
        The server tells every client what it is on connect, so an assistant is not starting blank.
        What it does not know is how you want to work. Paste this at the top of a chat and you will
        get a document you can watch being written, rather than a wall of text arriving at the end.
      </p>
      <CopyBox text={ASSISTANT_PROMPT} />
    </section>

    <!-- 4 ---------------------------------------------------- what it can do -->
    <section class="prose" aria-labelledby="tools">
      <div class="section-head"><h2 id="tools">What it can do</h2></div>
      <p>
        Twelve tools. Your client may list only a few of them at first and fetch the rest when a
        task needs one.
      </p>

      <h3>Writing</h3>
      <dl class="tools">
        <dt><code>batch</code></dt>
        <dd>Create a document, or apply several edits to one document atomically.</dd>
        <dt><code>update</code></dt>
        <dd>Edit a tab's contents, rename a document or a tab, or change a stored value.</dd>
        <dt><code>create</code></dt>
        <dd>Add an object inside a document — a comment, or an enum.</dd>
        <dt><code>delete</code></dt>
        <dd>Remove a comment. Not a document: deleting one is yours to do in the browser.</dd>
      </dl>

      <h3>Reading</h3>
      <dl class="tools">
        <dt><code>read</code></dt>
        <dd>Read a document and its tabs, a tab's contents, or a single comment.</dd>
        <dt><code>query</code></dt>
        <dd>List the comment history of a tab or a document — threads, replies, resolves.</dd>
        <dt><code>guide</code></dt>
        <dd>
          Fetch this server's own guides: how to create and edit documents, and what any refusal
          means.
        </dd>
      </dl>

      <h3>Asking you something</h3>
      <dl class="tools">
        <dt><code>ask</code></dt>
        <dd>
          Raise blocking questions when a fact is missing, each with two or three suggested options.
        </dd>
        <dt><code>wait</code></dt>
        <dd>Wait once for a person to answer — a single call, not a polling loop.</dd>
        <dt><code>questions</code></dt>
        <dd>
          List the questions on a document and their status; with no document, every open question
          you are allowed to see.
        </dd>
        <dt><code>answer</code></dt>
        <dd>
          Records <em>your</em> answer. An agent may not answer — not even its own question — and is
          refused if it tries.
        </dd>
      </dl>

      <h3>Taking it away</h3>
      <dl class="tools">
        <dt><code>export</code></dt>
        <dd>Render a document to markdown.</dd>
      </dl>
    </section>

    <!-- 5 ---------------------------------------------------------- tokens -->
    <section aria-labelledby="tokens">
      <div class="section-head"><h2 id="tokens">API tokens</h2></div>
      <p class="prose-lead">
        A token is what lets a client through the door. Give each one its own, so you can revoke a
        single client without breaking the rest.
      </p>

      {#if minted}
        <div class="reveal" role="alert">
          <p class="reveal-title">Here is your token — copy it now.</p>
          <p class="reveal-warning">
            <strong>This is the only time it will be shown.</strong> It is not stored anywhere it can
            be read back, here or on the server. If you lose it, revoke it and mint another.
          </p>
          <div class="secret-row">
            <code class="secret">{minted.token}</code>
            <button type="button" class="btn btn-quiet" onclick={() => copy(minted!.token, 'token')}>
              {copied === 'token' ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div class="options">
            {#if !useMinted}
              <button type="button" class="btn btn-quiet btn-small" onclick={useInSnippets}>
                Put it in the connect snippets
              </button>
            {/if}
            <button type="button" class="btn btn-quiet btn-small" onclick={forget}>
              I've saved it — hide this
            </button>
          </div>
        </div>
      {/if}

      {#if list.kind === 'unavailable'}
        <div class="notice">
          <p>Token minting is not available on this server yet.</p>
          <p>
            The <code>/v1/tokens</code> endpoints are not deployed here. Until they are, a token has
            to be minted from the command line with <code>make mint-token</code>.
          </p>
        </div>
      {:else if list.kind === 'stuck'}
        <p class="empty">You are signed in, but this account cannot manage tokens.</p>
        <p class="empty"><a href="/login">Go to sign in</a></p>
      {:else}
        <form onsubmit={mint}>
          <div class="field-row">
            <div class="field">
              <label class="field-label" for="label">Label <span class="req">*</span></label>
              <input
                class="input"
                id="label"
                name="label"
                type="text"
                maxlength={LABEL_MAX}
                placeholder="Claude Code on my laptop"
                autocomplete="off"
                required
                aria-invalid={labelError ? 'true' : undefined}
                aria-describedby="label-help"
                bind:value={label}
                oninput={() => (labelError = '')}
              />
            </div>
            <button type="submit" class="btn btn-primary" disabled={minting}>
              {minting ? 'Minting…' : 'Mint token'}
            </button>
          </div>
          <p class="field-help" id="label-help">
            Up to {LABEL_MAX} characters. <code>&lt;</code> and <code>&gt;</code> are not allowed.
          </p>
          {#if labelError}<p class="msg msg-err" role="alert">{labelError}</p>{/if}
        </form>
        {#if mintError}<p class="msg msg-err" role="alert">{mintError}</p>{/if}

        <div class="section-head">
          <h3>Existing tokens</h3>
        </div>

        {#if revokeError}<p class="msg msg-err" role="alert">{revokeError}</p>{/if}
        {#if revokeNote}<p class="msg msg-ok" role="status">{revokeNote}</p>{/if}

        {#if list.kind === 'loading'}
          <div aria-hidden="true">
            <div class="skeleton-line skeleton-a"></div>
            <div class="skeleton-line skeleton-b"></div>
          </div>
        {:else if list.kind === 'error'}
          <p class="empty">{list.message}</p>
          <div class="options">
            <button type="button" class="btn btn-quiet btn-small" onclick={load}>Try again</button>
          </div>
        {:else if list.kind === 'ready'}
          {#if list.tokens.length === 0}
            <p class="empty">
              No tokens yet. Mint one above and paste it into the snippets further up.
            </p>
          {:else}
            <ul class="token-list">
              {#each list.tokens as t (t.id)}
                <li>
                  <div class="token-main">
                    <div class="token-label">{t.label}</div>
                    <div class="token-meta">
                      <span>created {when(t.createdAt)}</span>
                      <!-- Coarse: updated roughly every five minutes, so it is a
                           date and never a presence indicator. -->
                      <span>last used {when(t.lastUsedAt)}</span>
                      {#if t.agentId}<span>agent {t.agentId}</span>{/if}
                    </div>
                  </div>
                  {#if confirming === t.id}
                    <div class="token-confirm">
                      <span class="token-confirm-text">Revoke? This breaks any agent using it.</span>
                      <button
                        type="button"
                        class="btn btn-danger btn-small"
                        disabled={revoking === t.id}
                        onclick={() => revoke(t.id, t.label)}
                      >
                        {revoking === t.id ? 'Revoking…' : 'Yes, revoke'}
                      </button>
                      <button
                        type="button"
                        class="btn btn-quiet btn-small"
                        disabled={revoking === t.id}
                        onclick={() => (confirming = null)}
                      >
                        Cancel
                      </button>
                    </div>
                  {:else}
                    <button
                      type="button"
                      class="btn btn-quiet btn-small"
                      onclick={() => {
                        confirming = t.id
                        revokeError = ''
                        revokeNote = ''
                      }}
                    >
                      Revoke
                    </button>
                  {/if}
                </li>
              {/each}
            </ul>
          {/if}
        {/if}
      {/if}
    </section>

    <p class="empty">
      <a href="/" onclick={(e) => navigate(e, '/')}>Back to all documents</a>
    </p>
  </div>
</main>

<style>
  /*
   * Prose on a settings page, held narrower than the shell.
   *
   * The container is 88rem, which is right for a document list and far too
   * wide for a paragraph -- but capping the text and left-aligning it inside a
   * full-width container is worse: a narrow column hugging the left edge with
   * a third of the page empty beside it. Documents already render their prose
   * at the container width and that is the established look, so these sections
   * match it rather than inventing a second measure.
   */
  .prose p,
  .prose-lead {
    font-size: var(--text-body);
    line-height: var(--text-body--line-height);
    margin: 0 0 1rem;
    text-wrap: pretty;
  }

  .prose-lead {
    color: var(--color-ink-soft);
    margin-bottom: 1.75rem;
  }

  .prose p code {
    font-family: var(--font-mono);
    font-size: 0.875em;
    background: var(--color-plinth);
    border-radius: 3px;
    padding: 0.1em 0.3em;
  }

  /*
   * "Existing tokens" under "API tokens". app.css styles `.section-head h2`
   * and nothing else, so an unstyled h3 landed at the browser's default and
   * read as loud as the section it sits under. Subordinate on purpose.
   */
  .section-head h3 {
    font-size: 1rem;
    letter-spacing: -0.015em;
    font-weight: 600;
    margin: 0;
  }

  .prose h3 {
    font-family: var(--font-mono);
    font-size: 0.6875rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--color-ink-soft);
    font-weight: 500;
    margin: 2rem 0 0.75rem;
  }

  /*
   * The tool list. A definition list rather than a table: each row is a name
   * and one sentence, and a table would spend a column rule and a header on
   * saying so.
   */
  .tools {
    margin: 0;
    border-top: 1px solid var(--color-line);
  }

  .tools dt {
    padding-top: 0.7rem;
  }

  .tools dt code {
    background: none;
    padding: 0;
    font-size: 0.8125rem;
    color: var(--ink);
    font-weight: 500;
  }

  .tools dd {
    margin: 0.1rem 0 0;
    padding-bottom: 0.7rem;
    border-bottom: 1px solid var(--color-line);
    color: var(--color-ink-soft);
    font-size: 0.9375rem;
    line-height: 1.5;
  }

  /* Two columns once there is room for the name to sit beside its sentence. */
  @media (min-width: 40rem) {
    .tools {
      display: grid;
      grid-template-columns: 9rem 1fr;
      column-gap: 1.5rem;
    }
  }

  /*
   * Twelve tools down one column leaves most of a wide page empty, which is
   * the problem this section just stopped having. Two name/description pairs
   * side by side fill it without stretching any single line.
   */
  @media (min-width: 68rem) {
    .tools {
      grid-template-columns: 9rem minmax(0, 1fr) 9rem minmax(0, 1fr);
      column-gap: 2.25rem;
    }

    .tools dt {
      padding-bottom: 0.7rem;
      border-bottom: 1px solid var(--color-line);
    }

    .tools dd {
      margin-top: 0;
      padding-top: 0.7rem;
    }
  }

  /*
   * The offer to put a live token in the snippets above.
   *
   * It is a row rather than a banner because it is a footnote to the endpoint
   * block, not an event; when a real token IS in the snippets it earns the
   * accent, because something on screen is now a credential.
   */
  .token-swap {
    display: flex;
    align-items: baseline;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin: 0 0 1.5rem;
  }

  .token-swap p {
    margin: 0;
    font-size: 0.8125rem;
    line-height: 1.5;
    color: var(--color-ink-soft);
  }

  .token-swap p.token-swap-live {
    color: var(--color-red-deep);
  }
</style>
